import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import api from '../../services/api';
import '../../styles/components.css';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2022 }, (_, i) => String(2023 + i));

// ─── All available scholar columns ────────────────────────────────────────────
const SCHOLAR_COLS = [
    // Basic Info
    { key: 'name', label: 'Scholar Name', group: 'Basic Info' },
    { key: 'rollno', label: 'TCE Roll No', group: 'Basic Info' },
    { key: 'dept', label: 'Department', group: 'Basic Info' },
    { key: 'supervisor', label: 'Supervisor', group: 'Basic Info' },
    { key: 'email', label: 'Email ID', group: 'Basic Info' },
    { key: 'phone', label: 'Phone Number', group: 'Basic Info' },
    { key: 'fellowshipStatus', label: 'Fellowship Status', group: 'Basic Info' },
    { key: 'phdStatus', label: 'PHD Status', group: 'Basic Info' },
    { key: 'month', label: 'Enrolled Month & Year', group: 'Basic Info' },
    { key: 'balCL', label: 'Balance CL', group: 'Basic Info' },
    { key: 'tclaim', label: 'Total Claim Amount (₹)', group: 'Basic Info' },
    // Bank Details
    { key: 'accountNumber', label: 'Account Number', group: 'Bank Details' },
    { key: 'bankName', label: 'Bank Name', group: 'Bank Details' },
    { key: 'branchName', label: 'Branch Name', group: 'Bank Details' },
    { key: 'ifscCode', label: 'IFSC Code', group: 'Bank Details' },
    // Dates
    { key: 'balCLExpire', label: 'Balance CL Expire', group: 'Dates' },
    { key: 'createdAt', label: 'Created At', group: 'Dates' },
    { key: 'updatedAt', label: 'Last Updated', group: 'Dates' },
];

const DEFAULT_COLS = new Set(['name', 'rollno', 'dept', 'supervisor']);

const fmtDate = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PUBLICATION_NUMERIC_COLS = new Set(['slNo', 'publishedOnSort']);

const PUBLICATION_COLS = [
    { key: 'scholarName', label: 'Scholar Name', group: 'Basic Info' },
    { key: 'rollNo', label: 'TCE Roll No', group: 'Basic Info' },
    { key: 'dept', label: 'Department', group: 'Basic Info' },
    { key: 'supervisor', label: 'Supervisor', group: 'Basic Info' },
    { key: 'email', label: 'Email ID', group: 'Basic Info' },
    { key: 'phone', label: 'Phone Number', group: 'Basic Info' },
    { key: 'fellowshipStatus', label: 'Fellowship Status', group: 'Basic Info' },
    { key: 'phdStatus', label: 'PHD Status', group: 'Basic Info' },
    { key: 'month', label: 'Enrolled Month & Year', group: 'Basic Info' },
    { key: 'balCL', label: 'Balance CL', group: 'Basic Info' },
    { key: 'title', label: 'Title', group: 'Publication Details' },
    { key: 'category', label: 'Category', group: 'Publication Details' },
    { key: 'venue', label: 'Journal/Conference Name', group: 'Publication Details' },
    { key: 'publishedOn', label: 'Published On', group: 'Publication Details' },
    { key: 'authors', label: 'Authors', group: 'Publication Details' },
    { key: 'doi', label: 'DOI', group: 'Publication Details' },
    { key: 'status', label: 'Status', group: 'Publication Details' },
    { key: 'scopusIndexed', label: 'Scopus Indexed', group: 'Publication Details' },
    { key: 'quartile', label: 'Quartile', group: 'Publication Details' },
    { key: 'trfAcknowledgement', label: 'TRF Acknowledgement', group: 'Publication Details' },
    { key: 'year', label: 'Year', group: 'Publication Details' },
];

const DEFAULT_PUBLICATION_COLS = new Set(['scholarName', 'rollNo', 'dept', 'title', 'category', 'venue', 'publishedOn', 'trfAcknowledgement', 'year']);

const formatPublicationCell = (row, key) => {
    const value = row[key];
    if (value == null || value === '') return '-';
    if (key === 'publishedOn') return String(value);
    if (key === 'scopusIndexed' || key === 'trfAcknowledgement') return value === true || value === 'Yes' ? 'Yes' : value === false || value === 'No' ? 'No' : String(value);
    return String(value);
};

const cellVal = (row, key) => {
    const v = row[key];
    if (v == null || v === '') return '-';
    if (key === 'tclaim') return `₹${Number(v).toLocaleString('en-IN')}`;
    if (key === 'balCLExpire' || key === 'createdAt' || key === 'updatedAt') return fmtDate(v);
    return String(v);
};

// ─── Scholar Report ────────────────────────────────────────────────────────────
const ScholarReport = () => {
    const [selectedCols, setSelectedCols] = useState(new Set(DEFAULT_COLS));
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [globalQ, setGlobalQ] = useState('');
    const [colFilters, setColFilters] = useState({});   // { key: filterString }
    const [selectedIds, setSelectedIds] = useState(new Set()); // set of slNo
    const [sortConfig, setSortConfig] = useState({ key: null, dir: null }); // asc | desc | null

    const groups = [...new Set(SCHOLAR_COLS.map(c => c.group))];
    const activeCols = SCHOLAR_COLS.filter(c => selectedCols.has(c.key));

    // ── Column selector toggles ──
    const toggleCol = (key) => setSelectedCols(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const selectAll = () => setSelectedCols(new Set(SCHOLAR_COLS.map(c => c.key)));
    const clearAll = () => setSelectedCols(new Set());

    // ── Sort cycle: same key asc→desc→off, new key starts asc ──
    const cycleSort = (key) => setSortConfig(prev => {
        if (prev.key !== key) return { key, dir: 'asc' };
        if (prev.dir === 'asc') return { key, dir: 'desc' };
        return { key: null, dir: null };
    });
    const sortIcon = (key) => {
        if (sortConfig.key !== key) return ' ⇅';
        return sortConfig.dir === 'asc' ? ' ▲' : ' ▼';
    };

    // ── Fetch ──
    const handleGenerate = async () => {
        if (selectedCols.size === 0) { alert('Please select at least one column.'); return; }
        setLoading(true);
        setGlobalQ('');
        setColFilters({});
        setSelectedIds(new Set());
        setSortConfig({ key: null, dir: null });
        try {
            const res = await api.get('/admin/reports/scholars-full');
            setRows(res.data.rows || []);
        } catch (err) {
            console.error('Error fetching scholar report:', err);
            alert('Failed to generate report. Please try again.');
        } finally { setLoading(false); }
    };

    // ── Filtering + Sorting ──
    const filteredRows = rows ? (() => {
        let r = rows.filter(row => {
            if (globalQ) {
                const q = globalQ.toLowerCase();
                if (!activeCols.some(c => String(row[c.key] ?? '').toLowerCase().includes(q))) return false;
            }
            for (const [key, val] of Object.entries(colFilters)) {
                if (!val) continue;
                if (!String(row[key] ?? '').toLowerCase().includes(val.toLowerCase())) return false;
            }
            return true;
        });
        if (sortConfig.key) {
            const { key, dir } = sortConfig;
            const mult = dir === 'asc' ? 1 : -1;
            r = [...r].sort((a, b) => {
                const av = String(a[key] ?? '').toLowerCase();
                const bv = String(b[key] ?? '').toLowerCase();
                const an = parseFloat(a[key]); const bn = parseFloat(b[key]);
                if (!isNaN(an) && !isNaN(bn)) return mult * (an - bn);
                return mult * av.localeCompare(bv);
            });
        }
        return r;
    })() : null;

    // ── Selection ──
    const allSelected = filteredRows && filteredRows.length > 0 && filteredRows.every(r => selectedIds.has(r.slNo));
    const toggleRow = (slNo) => setSelectedIds(prev => { const n = new Set(prev); n.has(slNo) ? n.delete(slNo) : n.add(slNo); return n; });
    const toggleAll = () => {
        const ids = (filteredRows || []).map(r => r.slNo);
        setSelectedIds(allSelected ? new Set() : new Set(ids));
    };

    // rows to export (selected subset or all filtered)
    const exportRows = selectedIds.size > 0
        ? (filteredRows || []).filter(r => selectedIds.has(r.slNo))
        : (filteredRows || []);

    // ── CSV Export ──
    const handleCSV = () => {
        if (!exportRows.length) return;
        const headers = activeCols.map(c => c.label);
        const csvRows = [
            ['Sl.No', ...headers].join(','),
            ...exportRows.map((r, i) => [
                i + 1,
                ...activeCols.map(c => {
                    const v = cellVal(r, c.key);
                    return `"${v.replace(/"/g, '""')}"`;
                })
            ].join(','))
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `TRF_Scholar_Report_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    // ── PDF Export (jsPDF) ──
    const handlePDF = async () => {
        if (!exportRows.length) return;
        const { jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // Logo
        const logoImg = new Image(); logoImg.src = '/tcelogo.png';
        await new Promise(r => { logoImg.onload = r; logoImg.onerror = r; });

        let y = 12;
        doc.addImage(logoImg, 'PNG', 14, 8, 20, 20);
        doc.setFontSize(15); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 58, 108);
        doc.text('THIAGARAJAR COLLEGE OF ENGINEERING', pageW / 2, y, { align: 'center' }); y += 7;
        doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
        doc.text('Thirupparankundram, Madurai - 625 015, Tamil Nadu', pageW / 2, y, { align: 'center' }); y += 5;
        doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(192, 57, 43);
        doc.text('THIAGARAJAR RESEARCH FELLOWSHIP (TRF) - SCHOLAR REPORT', pageW / 2, y, { align: 'center' }); y += 6;
        doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(51, 51, 51);
        doc.text(`Generated On: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, pageW / 2, y, { align: 'center' }); y += 4;

        doc.setDrawColor(26, 58, 108); doc.setLineWidth(0.8); doc.line(8, y, pageW - 8, y); y += 4;

        doc.setFillColor(240, 244, 255); doc.setDrawColor(204, 214, 246);
        doc.roundedRect(8, y, pageW - 16, 10, 2, 2, 'FD');
        doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 58, 108);
        doc.text(`Total Records: ${exportRows.length}     Columns: ${activeCols.map(c => c.label).join(', ')}`, 14, y + 7); y += 13;

        const head = [['Sl. No.', ...activeCols.map(c => c.label)]];
        const body = exportRows.map((r, i) => [i + 1, ...activeCols.map(c => cellVal(r, c.key))]);

        autoTable(doc, {
            theme: 'grid', startY: y,
            head, body,
            rowPageBreak: 'avoid',
            styles: { fontSize: 8, cellPadding: 2.5, valign: 'middle', textColor: [26, 26, 46], lineColor: [209, 217, 230], lineWidth: 0.2 },
            headStyles: { fillColor: [26, 58, 108], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [247, 249, 252] },
            columnStyles: { 0: { halign: 'center', cellWidth: 12 } },
            margin: { left: 8, right: 8 },
        });

        // ── Signature + footer: ensure they always fit on the last page ──
        const sigBlockHeight = 22 + 12; // gap(22) + sig line + label + footer(12)
        const tableEndY = doc.lastAutoTable.finalY;

        // If not enough room, push to a new page
        if (tableEndY + sigBlockHeight > pageH - 10) {
            doc.addPage();
        }

        const sigStartY = (tableEndY + sigBlockHeight > pageH - 10)
            ? 20
            : tableEndY + 22; // 22mm gap below table

        const sigLineW = 55;
        const sigPositions = [
            { x: pageW * 0.30, label: 'Associate Dean (Research)' },
            { x: pageW * 0.70, label: 'Principal' },
        ];



        doc.setDrawColor(51, 51, 51);
        doc.setLineWidth(0.5);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);

        sigPositions.forEach(({ x, label }) => {
            const lineStartX = x - sigLineW / 2;
            doc.line(lineStartX, sigStartY + 5, lineStartX + sigLineW, sigStartY + 5);
            doc.text(label, x, sigStartY + 10, { align: 'center' });
        });

        // ── Footer note ──
        const footY = sigStartY + 14;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(136, 136, 136);
        doc.text(
            'This is a computer-generated report from the TRF Management System - Thiagarajar College of Engineering',
            pageW / 2, footY + 5, { align: 'center' }
        );

        doc.save(`TRF_Scholar_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    return (
        <>
            {/* ── Column Selector ── */}
            <Card title="Scholar Report – Select Columns">
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={selectAll} style={{ padding: '0.35rem 0.85rem', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Select All</button>
                    <button onClick={clearAll} style={{ padding: '0.35rem 0.85rem', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Clear All</button>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{selectedCols.size} of {SCHOLAR_COLS.length} columns selected</span>
                </div>

                {groups.map(grp => (
                    <div key={grp} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>{grp}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '0.4rem' }}>
                            {SCHOLAR_COLS.filter(c => c.group === grp).map(c => (
                                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.83rem', cursor: 'pointer', padding: '0.3rem 0.5rem', borderRadius: '0.375rem', background: selectedCols.has(c.key) ? '#eff6ff' : 'transparent', border: '1px solid', borderColor: selectedCols.has(c.key) ? '#bfdbfe' : 'transparent' }}>
                                    <input type="checkbox" checked={selectedCols.has(c.key)} onChange={() => toggleCol(c.key)} style={{ accentColor: '#1d4ed8' }} />
                                    {c.label}
                                </label>
                            ))}
                        </div>
                    </div>
                ))}

                <div style={{ marginTop: '1rem' }}>
                    <Button variant="primary" onClick={handleGenerate} disabled={loading || selectedCols.size === 0}>
                        {loading ? 'Fetching...' : 'Generate Report'}
                    </Button>
                </div>
            </Card>

            {/* ── Results Table ── */}
            {rows !== null && (
                <Card
                    title={`Scholar Report (${filteredRows?.length ?? 0}${filteredRows?.length !== rows.length ? ` of ${rows.length}` : ''} record${rows.length !== 1 ? 's' : ''})`}
                    actions={
                        rows.length > 0 ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {selectedIds.size > 0 && <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600 }}>{selectedIds.size} selected</span>}
                                <Button variant="success" size="small" onClick={handleCSV} disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `Excel CSV (${selectedIds.size})` : 'Download CSV'}
                                </Button>
                                <Button variant="danger" size="small" onClick={handlePDF} disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `PDF (${selectedIds.size})` : 'Download PDF'}
                                </Button>
                            </div>
                        ) : null
                    }
                >
                    {rows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No scholars found.</p>
                    ) : (
                        <>
                            {/* Global search */}
                            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                <input
                                    type="text" className="form-input"
                                    placeholder={`Search across all columns (${activeCols.map(c => c.label).join(', ')})...`}
                                    value={globalQ}
                                    onChange={e => { setGlobalQ(e.target.value); setSelectedIds(new Set()); }}
                                    style={{ flex: 1, minWidth: '260px' }}
                                />
                                {globalQ && (
                                    <button onClick={() => { setGlobalQ(''); setSelectedIds(new Set()); }}
                                        style={{ padding: '0.5rem 0.85rem', background: '#e5e7eb', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}>
                                        ✕ Clear
                                    </button>
                                )}
                            </div>

                            {/* Selection info bar */}
                            {selectedIds.size > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.55rem 1rem', background: '#eff6ff', borderRadius: '0.5rem', marginBottom: '0.75rem', border: '1px solid #bfdbfe' }}>
                                    <span style={{ fontWeight: 600, color: '#1d4ed8' }}>{selectedIds.size} row{selectedIds.size !== 1 ? 's' : ''} selected — exports will include only selected rows</span>
                                    <button onClick={() => setSelectedIds(new Set())} style={{ padding: '0.3rem 0.75rem', background: '#e5e7eb', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', color: '#374151' }}>Clear selection</button>
                                </div>
                            )}

                            <div style={{ overflowX: 'auto' }}>
                                <table className="table" style={{ fontSize: '0.82rem', minWidth: 'max-content' }}>
                                    <thead>
                                        {/* Header row */}
                                        <tr>
                                            <th style={{ width: '36px', textAlign: 'center' }}>
                                                <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select/deselect all visible rows" />
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Sl. No.</th>
                                            {activeCols.map(c => (
                                                <th
                                                    key={c.key}
                                                    onClick={() => cycleSort(c.key)}
                                                    title={`Sort by ${c.label}`}
                                                    style={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    {c.label}{sortIcon(c.key)}
                                                </th>
                                            ))}
                                        </tr>
                                        {/* Per-column filter row */}
                                        <tr style={{ background: '#f8fafc' }}>
                                            <th></th>
                                            <th></th>
                                            {activeCols.map(c => (
                                                <th key={c.key} style={{ padding: '3px 4px' }}>
                                                    <input
                                                        type="text"
                                                        placeholder={`Filter...`}
                                                        value={colFilters[c.key] || ''}
                                                        onChange={e => {
                                                            setColFilters(prev => ({ ...prev, [c.key]: e.target.value }));
                                                            setSelectedIds(new Set());
                                                        }}
                                                        style={{ width: '100%', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.75rem', minWidth: '80px' }}
                                                    />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRows?.length === 0 ? (
                                            <tr><td colSpan={activeCols.length + 2} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No records match your search / filter.</td></tr>
                                        ) : filteredRows?.map((r, idx) => {
                                            const sel = selectedIds.has(r.slNo);
                                            return (
                                                <tr key={r.slNo} style={{ background: sel ? '#eff6ff' : undefined }}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={sel} onChange={() => toggleRow(r.slNo)} />
                                                    </td>
                                                    <td>{idx + 1}</td>
                                                    {activeCols.map(c => (
                                                        <td key={c.key} style={{ whiteSpace: (c.key === 'email' || c.key === 'accountNumber') ? 'nowrap' : undefined }}>
                                                            {cellVal(r, c.key)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Card>
            )}
        </>
    );
};

// ─── Monthly Claim Report ─────────────────────────────────────────────────────
// Status sort order for ascending/descending cycling
const STATUS_ORDER = [
    'Pending Supervisor',
    'Pending DLC',
    'Pending HOD',
    'Pending Associate Dean (R&D) ',
    'Associate Dean (R&D) Approved',
    'Rejected',
];

const getStatusStyle = (status) => {
    const map = {
        'Pending Supervisor': { background: '#fff3cd', color: '#856404' },
        'Pending DLC': { background: '#cce5ff', color: '#004085' },
        'Pending HOD': { background: '#d6d8f7', color: '#3d3f7c' },
        'Pending Associate Dean (R&D) ': { background: '#fde8d8', color: '#7c2d12' },
        'Associate Dean (R&D) Approved': { background: '#00ff44ff', color: '#383d41' },
        'Rejected': { background: '#f8d7da', color: '#721c24' },
    };
    return map[status] || { background: '#f0f0f0', color: '#555' };
};

const MonthlyClaimReport = () => {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [reportRows, setReportRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ field: null, dir: null }); // field: column key | null, dir: 'asc'|'desc'|null

    // Cycle sort: same field asc→desc→off, new field starts at asc
    const cycleSort = (field) => {
        setSortConfig(prev => {
            if (prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return { field: null, dir: null };
        });
    };



    // Returns ▲ / ▼ / ⇅ for a given column
    const sortIcon = (field) => {
        if (sortConfig.field !== field) return '⇅';
        return sortConfig.dir === 'asc' ? '▲' : '▼';
    };

    // Numeric columns
    const NUMERIC_COLS = new Set(['slNo', 'availedCL', 'balCL', 'llp', 'amount']);

    // Derived: filtered + sorted rows
    const filteredRows = reportRows
        ? (() => {
            let rows = reportRows.filter(r => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return (
                    r.tceRollNo?.toLowerCase().includes(q) ||
                    r.name?.toLowerCase().includes(q) ||
                    r.dept?.toLowerCase().includes(q) ||
                    r.status?.toLowerCase().includes(q) ||
                    r.enrolledMonthYear?.toLowerCase().includes(q)
                );
            });
            if (sortConfig.field) {
                const { field, dir } = sortConfig;
                const mult = dir === 'asc' ? 1 : -1;
                rows = [...rows].sort((a, b) => {
                    if (field === 'status') {
                        const ai = STATUS_ORDER.indexOf(a.status);
                        const bi = STATUS_ORDER.indexOf(b.status);
                        return mult * (ai - bi);
                    }
                    if (field === 'enrolledMonthYear') {
                        const getScore = (val) => {
                            if (!val) return 0;
                            const str = val.toLowerCase();
                            const yMatch = str.match(/\d{4}/);
                            const year = yMatch ? parseInt(yMatch[0], 10) : 0;
                            const mIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
                                .findIndex(m => str.includes(m));
                            return year * 12 + (mIndex !== -1 ? mIndex : 0);
                        };
                        return mult * (getScore(a[field]) - getScore(b[field]));
                    }
                    if (NUMERIC_COLS.has(field)) {
                        return mult * ((Number(a[field]) || 0) - (Number(b[field]) || 0));
                    }
                    const av = (a[field] ?? '').toString().toLowerCase();
                    const bv = (b[field] ?? '').toString().toLowerCase();
                    return mult * av.localeCompare(bv);
                });
            }
            return rows;
        })()
        : null;

    // Rows that will actually be exported (selected subset, or all filtered rows)
    const exportRows = selectedIds.size > 0
        ? (filteredRows || []).filter(r => selectedIds.has(r.slNo))
        : (filteredRows || []);

    const handleGenerate = async () => {
        if (!selectedMonth || !selectedYear) { alert('Please select both Month and Year'); return; }
        setLoading(true);
        setSearchQuery('');
        setSelectedIds(new Set());
        setSortConfig({ field: null, dir: null });
        try {
            const res = await api.get('/admin/reports/monthly-claims', {
                params: { month: selectedMonth, year: selectedYear }
            });
            setReportRows(res.data.rows || []);
        } catch (err) {
            console.error('Error fetching monthly claim report:', err);
            alert('Failed to generate report. Please try again.');
        } finally { setLoading(false); }
    };

    const toggleSelect = (slNo) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(slNo) ? next.delete(slNo) : next.add(slNo);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const allIds = (filteredRows || []).map(r => r.slNo);
        const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
        setSelectedIds(allSelected ? new Set() : new Set(allIds));
    };

    const allSelected = filteredRows && filteredRows.length > 0 &&
        filteredRows.every(r => selectedIds.has(r.slNo));

    /* ── CSV download (pure browser, no lib needed) ── */
    const handleDownloadCSV = () => {
        if (!exportRows || exportRows.length === 0) return;
        const headers = ['Sl.No', 'TCE Roll No', 'Name', 'Dept.', 'Enrolled Month & Year', 'Availed CL', 'Bal. CL', 'LLP', 'Amount (₹)', 'Status'];
        const csvRows = [
            headers.join(','),
            ...exportRows.map((r, idx) => [
                idx + 1, r.tceRollNo, `"${r.name}"`, `"${r.dept}"`, `"${r.enrolledMonthYear}"`,
                r.availedCL, r.balCL, r.llp, r.amount, `"${r.status}"`
            ].join(','))
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TRF_Monthly_Claims_${selectedMonth}_${selectedYear}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    /* ── PDF via jsPDF + autoTable (matches Attendance PDF style) ── */
    const handleDownloadPDF = async () => {
        if (!exportRows || exportRows.length === 0) return;

        const { jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();

        // Load Logo
        const logoImg = new Image();
        logoImg.src = '/tcelogo.png';
        await new Promise((resolve) => { logoImg.onload = resolve; logoImg.onerror = resolve; });

        let y = 12;

        // ── Header ──
        doc.addImage(logoImg, 'PNG', 14, 8, 20, 20);

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 58, 108);
        doc.text('THIAGARAJAR COLLEGE OF ENGINEERING', pageW / 2, y, { align: 'center' });
        y += 7;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(80, 80, 80);
        doc.text('Thirupparankundram, Madurai - 625 015, Tamil Nadu', pageW / 2, y, { align: 'center' });
        y += 6;

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(192, 57, 43);
        doc.text('THIAGARAJAR RESEARCH FELLOWSHIP (TRF) - MONTHLY CLAIMS REPORT', pageW / 2, y, { align: 'center' });
        y += 7;

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        doc.text(`Period: ${selectedMonth} ${selectedYear}`, pageW / 2, y, { align: 'center' });
        y += 5;

        // ── Divider ──
        doc.setDrawColor(26, 58, 108);
        doc.setLineWidth(0.8);
        doc.line(8, y, pageW - 8, y);
        y += 4;

        // ── Meta strip ──
        const totalAmount = exportRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        doc.setFillColor(240, 244, 255);
        doc.setDrawColor(204, 214, 246);
        doc.roundedRect(8, y, pageW - 16, 10, 2, 2, 'FD');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 58, 108);
        const genDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        doc.text(
            `Report Month: ${selectedMonth} ${selectedYear}     Total Claims: ${exportRows.length}     Total Amount: Rs.${totalAmount.toLocaleString('en-IN')}     Generated On: ${genDate}`,
            14, y + 7
        );
        y += 14;

        // ── Table body ──
        const body = exportRows.map((r, idx) => [
            idx + 1,
            r.tceRollNo || '-',
            r.name || '-',
            r.dept || '-',
            r.enrolledMonthYear || '-',
            r.availedCL ?? 0,
            r.balCL ?? 0,
            r.llp ?? 0,
            `Rs.${Number(r.amount || 0).toLocaleString('en-IN')}`,
            r.status || '-',
        ]);

        autoTable(doc, {
            theme: 'grid',
            startY: y,
            head: [['Sl. No.', 'TCE Roll No', 'Name', 'Dept.', 'Enrolled Month & Year',
                'Availed CL', 'Bal. CL', 'LLP', 'Amount', 'Status']],
            body,
            rowPageBreak: 'avoid',
            styles: { fontSize: 9, cellPadding: 2.8, valign: 'middle', fontStyle: 'normal', textColor: [26, 26, 46], lineColor: [209, 217, 230], lineWidth: 0.2 },
            headStyles: {
                fillColor: [26, 58, 108],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 9,
                lineColor: [26, 58, 108],
                lineWidth: 0.2,
            },
            alternateRowStyles: { fillColor: [247, 249, 252] },
            columnStyles: {
                0: { halign: 'center', cellWidth: 12 },
                5: { halign: 'center', cellWidth: 20 },
                6: { halign: 'center', cellWidth: 16 },
                7: { halign: 'center', cellWidth: 12 },
                8: { halign: 'right', cellWidth: 26, fontStyle: 'bold' },
                9: { cellWidth: 30 },
            },
            margin: { left: 8, right: 8 },
            foot: [[
                '', '', '', '', '', '', '', '',
                { content: `Rs.${totalAmount.toLocaleString('en-IN')}`, styles: { halign: 'right', fontStyle: 'bold' } },
                '',
            ]],
            footStyles: { fillColor: [26, 58, 108], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
            showFoot: 'lastPage',
        });

        // ── Signature + footer: ensure they always fit on the last page ──
        const pageH = doc.internal.pageSize.getHeight();
        const sigBlockHeight = 22 + 12; // gap(22) + sig line + label + footer(12)
        const tableEndY = doc.lastAutoTable.finalY;

        // If not enough room, push to a new page
        if (tableEndY + sigBlockHeight > pageH - 10) {
            doc.addPage();
        }

        const sigStartY = (tableEndY + sigBlockHeight > pageH - 10)
            ? 20
            : tableEndY + 22; // 22mm gap below table

        const sigLineW = 55;
        const sigPositions = [
            { x: pageW * 0.30, label: 'Associate Dean (Research)' },
            { x: pageW * 0.70, label: 'Principal' },
        ];

        // ── Verification note (above signatures) ──
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        doc.text(
            'The above claims have been verified by the Supervisor, DLC, forwarded by the respective Heads of the Departments and checked by the CLC (TRF).',
            pageW / 2, sigStartY - 17, { align: 'center' }
        );

        doc.setDrawColor(51, 51, 51);
        doc.setLineWidth(0.5);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);

        sigPositions.forEach(({ x, label }) => {
            const lineStartX = x - sigLineW / 2;
            doc.line(lineStartX, sigStartY + 5, lineStartX + sigLineW, sigStartY + 5);
            doc.text(label, x, sigStartY + 10, { align: 'center' });
        });

        // ── Footer note ──
        const footY = sigStartY + 14;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(136, 136, 136);
        doc.text(
            'This is a computer-generated report from the TRF Management System - Thiagarajar College of Engineering',
            pageW / 2, footY + 5, { align: 'center' }
        );

        doc.save(`TRF_Monthly_Claims_${selectedMonth}_${selectedYear}.pdf`);
    };

    return (
        <>
            <Card title="Monthly Claim Report – Filters">
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group">
                        <label className="form-label">Month</label>
                        <select className="form-input" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                            <option value="">-- Select Month --</option>
                            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Year</label>
                        <select className="form-input" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                            <option value="">-- Select Year --</option>
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <Button variant="primary" onClick={handleGenerate}
                            disabled={loading || !selectedMonth || !selectedYear}>
                            {loading ? 'Fetching...' : 'Generate Report'}
                        </Button>
                    </div>
                </div>
            </Card>

            {reportRows !== null && (
                <Card
                    title={`Claims for ${selectedMonth} ${selectedYear} (${filteredRows.length}${filteredRows.length !== reportRows.length ? ` of ${reportRows.length}` : ''} record${reportRows.length !== 1 ? 's' : ''})`}
                    actions={
                        reportRows.length > 0 ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {selectedIds.size > 0 && (
                                    <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600, marginRight: '0.5rem' }}>
                                        {selectedIds.size} selected
                                    </span>
                                )}
                                <Button variant="success" size="small" onClick={handleDownloadCSV}
                                    disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `CSV (${selectedIds.size})` : 'Download CSV'}
                                </Button>
                                <Button variant="danger" size="small" onClick={handleDownloadPDF}
                                    disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `PDF (${selectedIds.size})` : 'Download PDF'}
                                </Button>
                            </div>
                        ) : null
                    }
                >
                    {/* Search bar */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Search by name, roll no, dept, status, enrolled month..."
                            value={searchQuery}
                            onChange={e => { setSearchQuery(e.target.value); setSelectedIds(new Set()); }}
                            style={{ flex: 1, minWidth: '260px' }}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => { setSearchQuery(''); setSelectedIds(new Set()); }}
                                style={{
                                    padding: '0.5rem 0.85rem', background: '#e5e7eb',
                                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontSize: '0.875rem', color: '#374151'
                                }}
                            >
                                ✕ Clear
                            </button>
                        )}
                    </div>

                    {/* Selection info bar */}
                    {selectedIds.size > 0 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '1rem',
                            padding: '0.6rem 1rem', background: '#eff6ff',
                            borderRadius: '0.5rem', marginBottom: '0.75rem',
                            border: '1px solid #bfdbfe'
                        }}>
                            <span style={{ fontWeight: 600, color: '#1d4ed8' }}>
                                {selectedIds.size} claim{selectedIds.size !== 1 ? 's' : ''} selected — exports will include only selected rows
                            </span>
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                style={{
                                    padding: '0.3rem 0.75rem', background: '#e5e7eb',
                                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontSize: '0.8rem', color: '#374151'
                                }}
                            >
                                Clear selection
                            </button>
                        </div>
                    )}

                    {reportRows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                            No claims found for {selectedMonth} {selectedYear}.
                        </p>
                    ) : filteredRows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                            No claims match your search.
                        </p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table" style={{ fontSize: '0.85rem', minWidth: '960px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '36px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                title="Select / deselect all visible rows"
                                            />
                                        </th>
                                        {[
                                            { label: 'Sl. No.', field: 'slNo' },
                                            { label: 'TCE Roll No', field: 'tceRollNo' },
                                            { label: 'Name', field: 'name' },
                                            { label: 'Dept.', field: 'dept' },
                                            { label: 'Enrolled Month & Year', field: 'enrolledMonthYear' },
                                            { label: 'Availed CL', field: 'availedCL' },
                                            { label: 'Bal. CL', field: 'balCL' },
                                            { label: 'LLP', field: 'llp' },
                                            { label: 'Amount (₹)', field: 'amount' },
                                            { label: 'Status', field: 'status' },
                                        ].map(col => (
                                            <th
                                                key={col.field}
                                                onClick={() => cycleSort(col.field)}
                                                title={`Sort by ${col.label}`}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            >
                                                {col.label}&nbsp;{sortIcon(col.field)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((r, idx) => {
                                        const isSelected = selectedIds.has(r.slNo);
                                        const statusStyle = getStatusStyle(r.status);
                                        return (
                                            <tr key={r.slNo} style={{ background: isSelected ? '#eff6ff' : undefined }}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelect(r.slNo)}
                                                    />
                                                </td>
                                                <td>{idx + 1}</td>
                                                <td>{r.tceRollNo}</td>
                                                <td>{r.name}</td>
                                                <td>{r.dept}</td>
                                                <td>{r.enrolledMonthYear}</td>
                                                <td style={{ textAlign: 'center' }}>{r.availedCL}</td>
                                                <td style={{ textAlign: 'center' }}>{r.balCL}</td>
                                                <td style={{ textAlign: 'center' }}>{r.llp}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 600 }}>₹{Number(r.amount).toLocaleString('en-IN')}</td>
                                                <td>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '2px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.72rem',
                                                        fontWeight: 600,
                                                        ...statusStyle
                                                    }}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}
        </>
    );
};

const PublicationsReport = () => {
    const [selectedCols, setSelectedCols] = useState(new Set(DEFAULT_PUBLICATION_COLS));
    const [reportRows, setReportRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [globalQ, setGlobalQ] = useState('');
    const [colFilters, setColFilters] = useState({});
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ key: null, dir: null });

    const groups = [...new Set(PUBLICATION_COLS.map(c => c.group))];
    const activeCols = PUBLICATION_COLS.filter(c => selectedCols.has(c.key));

    const toggleCol = (key) => setSelectedCols(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
    const selectAll = () => setSelectedCols(new Set(PUBLICATION_COLS.map(c => c.key)));
    const clearAll = () => setSelectedCols(new Set());

    const cycleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key !== key) return { key, dir: 'asc' };
            if (prev.dir === 'asc') return { key, dir: 'desc' };
            return { key: null, dir: null };
        });
    };

    const sortIcon = (key) => {
        if (sortConfig.key !== key) return ' ⇅';
        return sortConfig.dir === 'asc' ? ' ▲' : ' ▼';
    };

    const filteredRows = reportRows
        ? (() => {
            let rows = reportRows.filter(row => {
                if (globalQ) {
                    const q = globalQ.toLowerCase();
                    if (!activeCols.some(c => String(row[c.key] ?? '').toLowerCase().includes(q))) return false;
                }
                for (const [key, val] of Object.entries(colFilters)) {
                    if (!val) continue;
                    if (!String(row[key] ?? '').toLowerCase().includes(val.toLowerCase())) return false;
                }
                return true;
            });

            if (sortConfig.key) {
                const { key, dir } = sortConfig;
                const mult = dir === 'asc' ? 1 : -1;
                rows = [...rows].sort((a, b) => {
                    if (key === 'publishedOn') {
                        return mult * ((Number(a.publishedOnSort) || 0) - (Number(b.publishedOnSort) || 0));
                    }
                    if (PUBLICATION_NUMERIC_COLS.has(key)) {
                        return mult * ((Number(a[key]) || 0) - (Number(b[key]) || 0));
                    }
                    const av = (a[key] ?? '').toString().toLowerCase();
                    const bv = (b[key] ?? '').toString().toLowerCase();
                    return mult * av.localeCompare(bv);
                });
            }

            return rows;
        })()
        : null;

    const exportRows = selectedIds.size > 0
        ? (filteredRows || []).filter(row => selectedIds.has(row.slNo))
        : (filteredRows || []);

    const handleGenerate = async () => {
        if (selectedCols.size === 0) {
            alert('Please select at least one column.');
            return;
        }

        setLoading(true);
        setGlobalQ('');
        setColFilters({});
        setSelectedIds(new Set());
        setSortConfig({ key: null, dir: null });

        try {
            const response = await api.get('/admin/reports/publications');
            setReportRows(response.data.rows || []);
        } catch (error) {
            const serverMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
            console.error('Error fetching publications report:', error.response?.status, error.response?.data || error);
            alert(`Failed to generate report: ${serverMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (slNo) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(slNo) ? next.delete(slNo) : next.add(slNo);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const allIds = (filteredRows || []).map(row => row.slNo);
        const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
        setSelectedIds(allSelected ? new Set() : new Set(allIds));
    };

    const allSelected = filteredRows && filteredRows.length > 0 && filteredRows.every(row => selectedIds.has(row.slNo));

    const handleDownloadCSV = () => {
        if (!exportRows || exportRows.length === 0) return;

        const headers = ['Sl.No', ...activeCols.map(c => c.label)];
        const csvRows = [
            headers.join(','),
            ...exportRows.map((row, index) => [
                index + 1,
                ...activeCols.map(c => `"${String(formatPublicationCell(row, c.key)).replace(/"/g, '""')}"`),
            ].join(','))
        ];

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TRF_Publications_Report_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadPDF = async () => {
        if (!exportRows || exportRows.length === 0) return;

        const { jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();

        const logoImg = new Image();
        logoImg.src = '/tcelogo.png';
        await new Promise((resolve) => { logoImg.onload = resolve; logoImg.onerror = resolve; });

        let y = 12;
        doc.addImage(logoImg, 'PNG', 14, 8, 20, 20);

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 58, 108);
        doc.text('THIAGARAJAR COLLEGE OF ENGINEERING', pageW / 2, y, { align: 'center' });
        y += 7;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(80, 80, 80);
        doc.text('Thirupparankundram, Madurai - 625 015, Tamil Nadu', pageW / 2, y, { align: 'center' });
        y += 6;

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(192, 57, 43);
        doc.text('THIAGARAJAR RESEARCH FELLOWSHIP (TRF) - PUBLICATIONS REPORT', pageW / 2, y, { align: 'center' });
        y += 7;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);
        doc.text(`Generated On: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, pageW / 2, y, { align: 'center' });
        y += 5;

        doc.setDrawColor(26, 58, 108);
        doc.setLineWidth(0.8);
        doc.line(8, y, pageW - 8, y);
        y += 4;

        const genDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        doc.setFillColor(240, 244, 255);
        doc.setDrawColor(204, 214, 246);
        doc.roundedRect(8, y, pageW - 16, 10, 2, 2, 'FD');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 58, 108);
        doc.text(`Total Records: ${exportRows.length}     Columns: ${activeCols.map(c => c.label).join(', ')}     Generated On: ${genDate}`, 14, y + 7);
        y += 14;

        const body = exportRows.map((row, index) => [index + 1, ...activeCols.map(c => formatPublicationCell(row, c.key))]);

        autoTable(doc, {
            theme: 'grid',
            startY: y,
            head: [['Sl. No.', ...activeCols.map(c => c.label)]],
            body,
            rowPageBreak: 'avoid',
            styles: { fontSize: 9.5, cellPadding: 3, valign: 'top', textColor: [30, 41, 59], lineColor: [209, 217, 230], lineWidth: 0.2 },
            headStyles: { fillColor: [30, 58, 138], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, halign: 'center' },
            alternateRowStyles: { fillColor: [247, 249, 252] },
            columnStyles: { 0: { halign: 'center', cellWidth: 10 } },
            margin: { top: 14, bottom: 18, left: 8, right: 8 },
        });

        const pageH = doc.internal.pageSize.getHeight();

        // ── Signature + footer: ensure they always fit on the last page ──
        const sigBlockHeight = 22 + 12; // gap(22) + sig line + label + footer(12)
        const tableEndY = doc.lastAutoTable.finalY;

        // If not enough room, push to a new page
        if (tableEndY + sigBlockHeight > pageH - 10) {
            doc.addPage();
        }

        const sigStartY = (tableEndY + sigBlockHeight > pageH - 10)
            ? 20
            : tableEndY + 22; // 22mm gap below table

        const sigLineW = 55;
        const sigPositions = [
            { x: pageW * 0.30, label: 'Associate Dean (Research)' },
            { x: pageW * 0.70, label: 'Principal' },
        ];



        doc.setDrawColor(51, 51, 51);
        doc.setLineWidth(0.5);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 51, 51);

        sigPositions.forEach(({ x, label }) => {
            const lineStartX = x - sigLineW / 2;
            doc.line(lineStartX, sigStartY + 5, lineStartX + sigLineW, sigStartY + 5);
            doc.text(label, x, sigStartY + 10, { align: 'center' });
        });

        // ── Footer note ──
        const footY = sigStartY + 14;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(136, 136, 136);
        doc.text(
            'This is a computer-generated report from the TRF Management System - Thiagarajar College of Engineering',
            pageW / 2, footY + 5, { align: 'center' }
        );

        doc.save(`TRF_Publications_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    return (
        <>
            <Card title="Publications Report – Select Columns">
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={selectAll} style={{ padding: '0.35rem 0.85rem', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Select All</button>
                    <button onClick={clearAll} style={{ padding: '0.35rem 0.85rem', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Clear All</button>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{selectedCols.size} of {PUBLICATION_COLS.length} columns selected</span>
                </div>

                {groups.map(grp => (
                    <div key={grp} style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>{grp}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '0.4rem' }}>
                            {PUBLICATION_COLS.filter(c => c.group === grp).map(c => (
                                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.83rem', cursor: 'pointer', padding: '0.3rem 0.5rem', borderRadius: '0.375rem', background: selectedCols.has(c.key) ? '#eff6ff' : 'transparent', border: '1px solid', borderColor: selectedCols.has(c.key) ? '#bfdbfe' : 'transparent' }}>
                                    <input type="checkbox" checked={selectedCols.has(c.key)} onChange={() => toggleCol(c.key)} style={{ accentColor: '#1d4ed8' }} />
                                    {c.label}
                                </label>
                            ))}
                        </div>
                    </div>
                ))}

                <div style={{ marginTop: '1rem' }}>
                    <Button variant="primary" onClick={handleGenerate} disabled={loading || selectedCols.size === 0}>
                        {loading ? 'Fetching...' : 'Generate Report'}
                    </Button>
                </div>
            </Card>

            {reportRows !== null && (
                <Card
                    title={`Publications Report (${filteredRows.length}${filteredRows.length !== reportRows.length ? ` of ${reportRows.length}` : ''} record${reportRows.length !== 1 ? 's' : ''})`}
                    actions={
                        reportRows.length > 0 ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {selectedIds.size > 0 && (
                                    <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600, marginRight: '0.5rem' }}>
                                        {selectedIds.size} selected
                                    </span>
                                )}
                                <Button variant="success" size="small" onClick={handleDownloadCSV} disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `CSV (${selectedIds.size})` : 'Download CSV'}
                                </Button>
                                <Button variant="danger" size="small" onClick={handleDownloadPDF} disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `PDF (${selectedIds.size})` : 'Download PDF'}
                                </Button>
                            </div>
                        ) : null
                    }
                >
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            className="form-input"
                            placeholder={`Search across all columns (${activeCols.map(c => c.label).join(', ')})...`}
                            value={globalQ}
                            onChange={e => { setGlobalQ(e.target.value); setSelectedIds(new Set()); }}
                            style={{ flex: 1, minWidth: '260px' }}
                        />
                        {globalQ && (
                            <button
                                onClick={() => { setGlobalQ(''); setSelectedIds(new Set()); }}
                                style={{
                                    padding: '0.5rem 0.85rem', background: '#e5e7eb',
                                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontSize: '0.875rem', color: '#374151'
                                }}
                            >
                                ✕ Clear
                            </button>
                        )}
                    </div>

                    {selectedIds.size > 0 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '1rem',
                            padding: '0.6rem 1rem', background: '#eff6ff',
                            borderRadius: '0.5rem', marginBottom: '0.75rem',
                            border: '1px solid #bfdbfe'
                        }}>
                            <span style={{ fontWeight: 600, color: '#1d4ed8' }}>
                                {selectedIds.size} publication{selectedIds.size !== 1 ? 's' : ''} selected — exports will include only selected rows
                            </span>
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                style={{
                                    padding: '0.3rem 0.75rem', background: '#e5e7eb',
                                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontSize: '0.8rem', color: '#374151'
                                }}
                            >
                                Clear selection
                            </button>
                        </div>
                    )}

                    {reportRows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                            No publications found.
                        </p>
                    ) : filteredRows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                            No publications match your search / filter.
                        </p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table" style={{ fontSize: '0.85rem', minWidth: '1200px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '36px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                title="Select / deselect all visible rows"
                                            />
                                        </th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Sl. No.</th>
                                        {activeCols.map(col => (
                                            <th
                                                key={col.key}
                                                onClick={() => cycleSort(col.key)}
                                                title={`Sort by ${col.label}`}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            >
                                                {col.label}{sortIcon(col.key)}
                                            </th>
                                        ))}
                                    </tr>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <th></th>
                                        <th></th>
                                        {activeCols.map(col => (
                                            <th key={col.key} style={{ padding: '3px 4px' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Filter..."
                                                    value={colFilters[col.key] || ''}
                                                    onChange={e => {
                                                        setColFilters(prev => ({ ...prev, [col.key]: e.target.value }));
                                                        setSelectedIds(new Set());
                                                    }}
                                                    style={{ width: '100%', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.75rem', minWidth: '80px' }}
                                                />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((row, idx) => {
                                        const isSelected = selectedIds.has(row.slNo);
                                        return (
                                            <tr key={row.slNo} style={{ background: isSelected ? '#eff6ff' : undefined }}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelect(row.slNo)}
                                                    />
                                                </td>
                                                <td>{idx + 1}</td>
                                                {activeCols.map(col => (
                                                    <td key={col.key} style={{ whiteSpace: (col.key === 'doi' || col.key === 'authors') ? 'nowrap' : undefined }}>
                                                        {formatPublicationCell(row, col.key)}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td></td>
                                        <td colSpan={activeCols.length} style={{ textAlign: 'right', fontWeight: 700, padding: '0.5rem' }}>Total Publications</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                            {((selectedIds.size > 0 ? exportRows : filteredRows) || []).length}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </Card>
            )}
        </>
    );
};

// ─── Monthly Attendance Report ───────────────────────────────────────────────
const MonthlyAttendanceReport = () => {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [reportRows, setReportRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ field: null, dir: null });

    const cycleSort = (field) => {
        setSortConfig(prev => {
            if (prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return { field: null, dir: null };
        });
    };
    const sortIcon = (field) => {
        if (sortConfig.field !== field) return '⇅';
        return sortConfig.dir === 'asc' ? '▲' : '▼';
    };

    const ATT_NUMERIC = new Set(['availedCL', 'balCL', 'llp']);

    const parseEnrolledScore = (val) => {
        if (!val) return 0;
        const str = val.toLowerCase();
        const yMatch = str.match(/\d{4}/);
        const year = yMatch ? parseInt(yMatch[0], 10) : 0;
        const mIndex = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
            .findIndex(m => str.includes(m));
        return year * 12 + (mIndex !== -1 ? mIndex : 0);
    };

    const filteredRows = reportRows
        ? (() => {
            let rows = reportRows.filter(r => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return (
                    r.tceRollNo?.toLowerCase().includes(q) ||
                    r.name?.toLowerCase().includes(q) ||
                    r.dept?.toLowerCase().includes(q) ||
                    r.enrolledMonthYear?.toLowerCase().includes(q)
                );
            });
            if (sortConfig.field) {
                const { field, dir } = sortConfig;
                const mult = dir === 'asc' ? 1 : -1;
                rows = [...rows].sort((a, b) => {
                    if (field === 'enrolledMonthYear') {
                        return mult * (parseEnrolledScore(a[field]) - parseEnrolledScore(b[field]));
                    }
                    if (ATT_NUMERIC.has(field)) {
                        return mult * ((Number(a[field]) || 0) - (Number(b[field]) || 0));
                    }
                    const av = (a[field] ?? '').toString().toLowerCase();
                    const bv = (b[field] ?? '').toString().toLowerCase();
                    return mult * av.localeCompare(bv);
                });
            }
            return rows;
        })()
        : null;

    const exportRows = selectedIds.size > 0
        ? (filteredRows || []).filter(r => selectedIds.has(r.slNo))
        : (filteredRows || []);

    const handleGenerate = async () => {
        if (!selectedMonth || !selectedYear) { alert('Please select both Month and Year'); return; }
        setLoading(true);
        setSearchQuery('');
        setSelectedIds(new Set());
        setSortConfig({ field: null, dir: null });
        try {
            const res = await api.get('/admin/reports/monthly-attendance', {
                params: { month: selectedMonth, year: selectedYear }
            });
            setReportRows(res.data.rows || []);
        } catch (err) {
            console.error('Error fetching monthly attendance report:', err);
            alert('Failed to generate report. Please try again.');
        } finally { setLoading(false); }
    };

    const toggleSelect = (slNo) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(slNo) ? next.delete(slNo) : next.add(slNo);
            return next;
        });
    };
    const toggleSelectAll = () => {
        const allIds = (filteredRows || []).map(r => r.slNo);
        const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
        setSelectedIds(allSelected ? new Set() : new Set(allIds));
    };
    const allSelected = filteredRows && filteredRows.length > 0 &&
        filteredRows.every(r => selectedIds.has(r.slNo));

    /* ── CSV ── */
    const handleDownloadCSV = () => {
        if (!exportRows || exportRows.length === 0) return;
        const headers = ['Sl.No', 'TCE Roll No', 'Name', 'Dept.', 'Enrolled Month & Year',
            'Availed CL', 'Bal. CL', 'LLP', 'Attendance Report (Drive Link)'];
        const csvRows = [
            headers.join(','),
            ...exportRows.map((r, idx) => [
                idx + 1,
                r.tceRollNo,
                `"${r.name}"`,
                `"${r.dept}"`,
                `"${r.enrolledMonthYear}"`,
                r.availedCL,
                r.balCL,
                r.llp,
                `"${r.attendanceCertificate || ''}"`,
            ].join(','))
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TRF_Attendance_Report_${selectedMonth}_${selectedYear}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    /* ── PDF via jsPDF + autoTable (vector-crisp text, real clickable links) ── */
    const handleDownloadPDF = async () => {
        if (!exportRows || exportRows.length === 0) return;

        const { jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();

        // Load Logo Image
        const logoImg = new Image();
        logoImg.src = '/tcelogo.png';
        await new Promise((resolve) => {
            logoImg.onload = resolve;
            logoImg.onerror = resolve;
        });

        let y = 12;

        // ── Header ──
        // Add logo
        doc.addImage(logoImg, 'PNG', 14, 8, 20, 20);

        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 58, 108);
        doc.text('THIAGARAJAR COLLEGE OF ENGINEERING', pageW / 2, y, { align: 'center' });
        y += 6;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text('Thirupparankundram, Madurai - 625 015, Tamil Nadu', pageW / 2, y, { align: 'center' });
        y += 5;

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(13, 108, 62);
        doc.text('THIAGARAJAR RESEARCH FELLOWSHIP (TRF) - MONTHLY ATTENDANCE REPORT', pageW / 2, y, { align: 'center' });
        y += 6;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 51, 51);
        doc.text(`Period: ${selectedMonth} ${selectedYear}`, pageW / 2, y, { align: 'center' });
        y += 4;

        // ── Divider ──
        doc.setDrawColor(26, 58, 108);
        doc.setLineWidth(0.8);
        doc.line(8, y, pageW - 8, y);
        y += 4;

        // ── Meta strip ──
        doc.setFillColor(240, 248, 244);
        doc.setDrawColor(178, 223, 219);
        doc.roundedRect(8, y, pageW - 16, 8, 2, 2, 'FD');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(13, 108, 62);
        const genDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        doc.text(`Report Month: ${selectedMonth} ${selectedYear}     Total Records: ${exportRows.length}     Generated On: ${genDate}`, 14, y + 5);
        y += 12;

        // ── Table body data ──
        const body = exportRows.map((r, idx) => [
            idx + 1,
            r.tceRollNo || '-',
            r.name || '-',
            r.dept || '-',
            r.enrolledMonthYear || '-',
            r.availedCL ?? 0,
            r.balCL ?? 0,
            r.llp ?? 0,
            r.attendanceCertificate ? 'View Link' : 'Not Uploaded',
        ]);

        // ── autoTable ──
        autoTable(doc, {
            theme: 'grid',
            startY: y,
            head: [['Sl. No.', 'TCE Roll No', 'Name', 'Dept.', 'Enrolled Month & Year',
                'Availed CL', 'Bal. CL', 'LLP', 'Attendance Report (Drive Link)']],
            body,
            styles: { fontSize: 8, cellPadding: 2.5, valign: 'middle', textColor: [26, 26, 46], lineColor: [209, 217, 230], lineWidth: 0.1 },
            headStyles: {
                fillColor: [13, 108, 62],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 8,
                lineColor: [13, 108, 62],
                lineWidth: 0.1
            },
            alternateRowStyles: { fillColor: [242, 251, 247] },
            columnStyles: {
                0: { halign: 'center', cellWidth: 12 },
                5: { halign: 'center', cellWidth: 20 },
                6: { halign: 'center', cellWidth: 16 },
                7: { halign: 'center', cellWidth: 12 },
                8: { cellWidth: 38, textColor: [26, 86, 219], fontStyle: 'bold' },
            },
            margin: { left: 8, right: 8 },
            didDrawCell: (data) => {
                // Inject clickable link annotation over each "View Certificate" cell
                if (data.section === 'body' && data.column.index === 8) {
                    const rowIdx = data.row.index;
                    const url = exportRows[rowIdx]?.attendanceCertificate;
                    if (url) {
                        doc.link(
                            data.cell.x,
                            data.cell.y,
                            data.cell.width,
                            data.cell.height,
                            { url, target: '_blank' }
                        );
                    }
                }
            }
        });

        // ── Footer ──
        const finalY = doc.lastAutoTable.finalY + 6;
        doc.setFontSize(7);
        doc.setTextColor(136, 136, 136);
        doc.setFont('helvetica', 'normal');
        doc.text(
            'This is a computer-generated report from the TRF Management System - Thiagarajar College of Engineering',
            pageW / 2,
            finalY,
            { align: 'center' }
        );

        doc.save(`TRF_Attendance_Report_${selectedMonth}_${selectedYear}.pdf`);
    };

    return (
        <>
            <Card title="Monthly Attendance Report – Filters">
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group">
                        <label className="form-label">Month</label>
                        <select className="form-input" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                            <option value="">-- Select Month --</option>
                            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Year</label>
                        <select className="form-input" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                            <option value="">-- Select Year --</option>
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <Button variant="primary" onClick={handleGenerate}
                            disabled={loading || !selectedMonth || !selectedYear}>
                            {loading ? 'Fetching...' : 'Generate Report'}
                        </Button>
                    </div>
                </div>
            </Card>

            {reportRows !== null && (
                <Card
                    title={`Attendance for ${selectedMonth} ${selectedYear} (${filteredRows.length}${filteredRows.length !== reportRows.length ? ` of ${reportRows.length}` : ''} record${reportRows.length !== 1 ? 's' : ''})`}
                    actions={
                        reportRows.length > 0 ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {selectedIds.size > 0 && (
                                    <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600, marginRight: '0.5rem' }}>
                                        {selectedIds.size} selected
                                    </span>
                                )}
                                <Button variant="success" size="small" onClick={handleDownloadCSV}
                                    disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `CSV (${selectedIds.size})` : 'Download CSV'}
                                </Button>
                                <Button variant="danger" size="small" onClick={handleDownloadPDF}
                                    disabled={exportRows.length === 0}>
                                    ⬇ {selectedIds.size > 0 ? `PDF (${selectedIds.size})` : 'Download PDF'}
                                </Button>
                            </div>
                        ) : null
                    }
                >
                    {/* Search bar */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Search by name, roll no, dept, enrolled month..."
                            value={searchQuery}
                            onChange={e => { setSearchQuery(e.target.value); setSelectedIds(new Set()); }}
                            style={{ flex: 1, minWidth: '260px' }}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => { setSearchQuery(''); setSelectedIds(new Set()); }}
                                style={{
                                    padding: '0.5rem 0.85rem', background: '#e5e7eb',
                                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontSize: '0.875rem', color: '#374151'
                                }}
                            >
                                ✕ Clear
                            </button>
                        )}
                    </div>

                    {/* Selection info bar */}
                    {selectedIds.size > 0 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '1rem',
                            padding: '0.6rem 1rem', background: '#f0fdf4',
                            borderRadius: '0.5rem', marginBottom: '0.75rem',
                            border: '1px solid #bbf7d0'
                        }}>
                            <span style={{ fontWeight: 600, color: '#059669' }}>
                                {selectedIds.size} record{selectedIds.size !== 1 ? 's' : ''} selected — exports will include only selected rows
                            </span>
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                style={{
                                    padding: '0.3rem 0.75rem', background: '#e5e7eb',
                                    border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontSize: '0.8rem', color: '#374151'
                                }}
                            >
                                Clear selection
                            </button>
                        </div>
                    )}

                    {reportRows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                            No attendance records found for {selectedMonth} {selectedYear}.
                        </p>
                    ) : filteredRows.length === 0 ? (
                        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>
                            No records match your search.
                        </p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table" style={{ fontSize: '0.85rem', minWidth: '960px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '36px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                title="Select / deselect all visible rows"
                                            />
                                        </th>
                                        {[
                                            { label: 'Sl. No.', field: 'slNo' },
                                            { label: 'TCE Roll No', field: 'tceRollNo' },
                                            { label: 'Name', field: 'name' },
                                            { label: 'Dept.', field: 'dept' },
                                            { label: 'Enrolled Month & Year', field: 'enrolledMonthYear' },
                                            { label: 'Availed CL', field: 'availedCL' },
                                            { label: 'Bal. CL', field: 'balCL' },
                                            { label: 'LLP', field: 'llp' },
                                            { label: 'Attendance Report', field: 'attendanceCertificate' },
                                        ].map(col => (
                                            <th
                                                key={col.field}
                                                onClick={() => cycleSort(col.field)}
                                                title={`Sort by ${col.label}`}
                                                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                            >
                                                {col.label}&nbsp;{sortIcon(col.field)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((r, idx) => {
                                        const isSelected = selectedIds.has(r.slNo);
                                        return (
                                            <tr key={r.slNo} style={{ background: isSelected ? '#f0fdf4' : undefined }}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelect(r.slNo)}
                                                    />
                                                </td>
                                                <td>{idx + 1}</td>
                                                <td>{r.tceRollNo}</td>
                                                <td>{r.name}</td>
                                                <td>{r.dept}</td>
                                                <td>{r.enrolledMonthYear}</td>
                                                <td style={{ textAlign: 'center' }}>{r.availedCL}</td>
                                                <td style={{ textAlign: 'center' }}>{r.balCL}</td>
                                                <td style={{ textAlign: 'center' }}>{r.llp}</td>
                                                <td>
                                                    {r.attendanceCertificate ? (
                                                        <a
                                                            href={r.attendanceCertificate}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#1a56db',
                                                                fontWeight: 600,
                                                                fontSize: '0.8rem',
                                                                textDecoration: 'none',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '0.25rem'
                                                            }}
                                                        >
                                                            🔗 View
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: '#9ca3af', fontSize: '0.78rem', fontStyle: 'italic' }}>
                                                            Not uploaded
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}
        </>
    );
};

// ─── Main AdminReports Component ──────────────────────────────────────────────
const AdminReports = () => {
    const [activeTab, setActiveTab] = useState('scholar');

    const tabStyle = (tab) => ({
        padding: '0.6rem 1.5rem',
        borderRadius: '8px 8px 0 0',
        border: '1px solid #e5e7eb',
        borderBottom: activeTab === tab ? '2px solid #fff' : '1px solid #e5e7eb',
        background: activeTab === tab ? '#fff' : '#f9fafb',
        color: activeTab === tab ? '#1d4ed8' : '#6b7280',
        fontWeight: activeTab === tab ? 700 : 500,
        cursor: 'pointer',
        fontSize: '0.9rem',
        marginRight: '4px',
        transition: 'all 0.2s'
    });

    return (
        <Layout title="Reports">
            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '1.5rem' }}>
                <button style={tabStyle('scholar')} onClick={() => setActiveTab('scholar')}>
                    📊 Scholar Reports
                </button>
                <button style={tabStyle('claims')} onClick={() => setActiveTab('claims')}>
                    📅 Monthly Claim Report
                </button>
                <button style={tabStyle('attendance')} onClick={() => setActiveTab('attendance')}>
                    🗂️ Monthly Attendance Report
                </button>
                <button style={tabStyle('publications')} onClick={() => setActiveTab('publications')}>
                    📚 Publications Report
                </button>

            </div>

            {activeTab === 'scholar' && <ScholarReport />}
            {activeTab === 'claims' && <MonthlyClaimReport />}
            {activeTab === 'publications' && <PublicationsReport />}
            {activeTab === 'attendance' && <MonthlyAttendanceReport />}
        </Layout>
    );
};

export default AdminReports;
